import { useRef, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { MediaBox } from "./MediaBox";
import { FILMSTRIP } from "@/constants/testIds";

const declaredRatio = (ratio) => (ratio === "16:9" ? 16 / 9 : ratio === "9:16" ? 9 / 16 : 1);

export const Filmstrip = ({ projects, searchString, linkBase = "/work", useNaturalRatio = true }) => {
  const containerRef = useRef(null);
  const itemRefs = useRef([]);
  const dragState = useRef({ isDown: false, startX: 0, startScroll: 0 });
  const [activeIndex, setActiveIndex] = useState(0);
  const [naturalRatios, setNaturalRatios] = useState({});
  const reduceMotion = useReducedMotion();

  const handleImgLoad = (slug) => (e) => {
    if (!useNaturalRatio) return;
    const { naturalWidth, naturalHeight } = e.target;
    if (naturalWidth && naturalHeight) {
      setNaturalRatios((prev) => ({ ...prev, [slug]: naturalWidth / naturalHeight }));
    }
  };

  const recalcActive = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const center = container.scrollLeft + container.clientWidth / 2;
    let bestIdx = 0;
    let bestDist = Infinity;
    itemRefs.current.forEach((el, i) => {
      if (!el) return;
      const elCenter = el.offsetLeft + el.offsetWidth / 2;
      const dist = Math.abs(elCenter - center);
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = i;
      }
    });
    setActiveIndex(bestIdx);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        recalcActive();
        raf = null;
      });
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    recalcActive();
    return () => container.removeEventListener("scroll", onScroll);
  }, [projects, recalcActive]);

  const scrollToIndex = useCallback(
    (idx) => {
      const clamped = Math.max(0, Math.min(projects.length - 1, idx));
      const el = itemRefs.current[clamped];
      if (el) {
        el.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", inline: "center", block: "nearest" });
      }
    },
    [projects.length, reduceMotion]
  );

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "ArrowRight") scrollToIndex(activeIndex + 1);
      if (e.key === "ArrowLeft") scrollToIndex(activeIndex - 1);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeIndex, scrollToIndex]);

  const onPointerDown = (e) => {
    const container = containerRef.current;
    if (!container) return;
    dragState.current = { isDown: true, startX: e.clientX, startScroll: container.scrollLeft };
    container.classList.add("cursor-grabbing");
  };
  const onPointerMove = (e) => {
    if (!dragState.current.isDown) return;
    const container = containerRef.current;
    const dx = e.clientX - dragState.current.startX;
    container.scrollLeft = dragState.current.startScroll - dx;
  };
  const endDrag = () => {
    dragState.current.isDown = false;
    containerRef.current?.classList.remove("cursor-grabbing");
  };

  if (projects.length === 0) {
    return (
      <div
        data-testid="filmstrip-empty-state"
        className="flex items-center justify-center h-[50vh] font-mono text-sm uppercase tracking-widest text-[#888880]"
      >
        No projects match these filters
      </div>
    );
  }

  return (
    <div className="relative w-full">
      <div
        ref={containerRef}
        data-testid={FILMSTRIP.container}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        role="listbox"
        aria-label="Project collection filmstrip"
        className="no-scrollbar flex items-center gap-3 sm:gap-4 md:gap-6 overflow-x-auto overscroll-x-contain px-[6vw] sm:px-[10vw] snap-x snap-mandatory cursor-grab select-none h-[52vh] sm:h-[60vh] md:h-[66vh] lg:h-[70vh]"
      >
        {projects.map((p, i) => (
          <motion.div
            key={p.slug}
            ref={(el) => (itemRefs.current[i] = el)}
            initial={reduceMotion ? false : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: reduceMotion ? 0 : Math.min(i, 8) * 0.05, ease: [0.16, 1, 0.3, 1] }}
            className="snap-center shrink-0 h-full flex flex-col"
            style={{ aspectRatio: naturalRatios[p.slug] || declaredRatio(p.aspectRatio) }}
            role="option"
            aria-selected={i === activeIndex}
            data-testid={FILMSTRIP.panel(i)}
          >
            <Link
              to={`${linkBase}/${p.slug}`}
              state={{ fromSearch: searchString }}
              className={`relative block flex-1 min-h-0 overflow-hidden border transition-all duration-500 ease-out ${
                i === activeIndex
                  ? "border-[#CCFF00]/70 shadow-[0_0_40px_rgba(204,255,0,0.12)] grayscale-0 opacity-100 scale-100"
                  : "border-[#2A2A2A] grayscale contrast-125 opacity-45 scale-[0.94] hover:opacity-75 hover:grayscale-0"
              }`}
            >
              <MediaBox project={p} priority={i < 3} onImgLoad={handleImgLoad(p.slug)} />
              {p.isDemo && (
                <span className="absolute top-2 right-2 sm:top-3 sm:right-3 font-mono text-[10px] tracking-widest uppercase bg-black/70 text-[#CCFF00] px-2 py-1 border border-[#CCFF00]/40">
                  Demo
                </span>
              )}
            </Link>
            <div className="mt-2 sm:mt-3 flex items-center justify-between font-mono text-[10px] sm:text-xs tracking-widest uppercase text-[#F5F5F0]">
              <span className="truncate">{p.title}</span>
              <span className="text-[#888880] shrink-0 ml-2">{p.category}</span>
            </div>
          </motion.div>
        ))}
      </div>
      <button
        type="button"
        aria-label="Previous project"
        data-testid={FILMSTRIP.prevArrow}
        onClick={() => scrollToIndex(activeIndex - 1)}
        className="hidden sm:flex items-center justify-center absolute left-1 sm:left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 border border-[#2A2A2A] text-[#F5F5F0] hover:border-[#CCFF00] hover:text-[#CCFF00] transition-colors z-10"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        type="button"
        aria-label="Next project"
        data-testid={FILMSTRIP.nextArrow}
        onClick={() => scrollToIndex(activeIndex + 1)}
        className="hidden sm:flex items-center justify-center absolute right-1 sm:right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 border border-[#2A2A2A] text-[#F5F5F0] hover:border-[#CCFF00] hover:text-[#CCFF00] transition-colors z-10"
      >
        <ChevronRight size={20} />
      </button>
    </div>
  );
};

export default Filmstrip;
